import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { CancelledError, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@carepass/api-client";
import { Dialog } from "@carepass/ui";
import { memberRouter, memberSessionStore } from "../../app/runtime";
import { memberApi, publicApi } from "../shared/api";
import type { TermAgreement } from "../shared/types";
import { LegalDocument } from "../shared/MemberLayouts";
import { Alert, Button, EmptyState, ErrorState, LoadingState, Surface, dateTime, navigate } from "../shared/ui";
import { MY_PRIVACY_POLICY_PATH } from "../public/public-document";
import "./MemberTermsPage.css";

type AgreementChange = { readonly termVersionId: number; readonly agreed: boolean };
type FailedChange = { readonly request: AgreementChange; readonly message: string };

function AgreementStatus({ term }: { readonly term: TermAgreement }) {
  return <>
    <small>{term.agreed ? "동의 완료" : "미동의"} · {term.agreed ? "동의 버전" : "버전"} {term.versionNo}</small>
    {term.agreedAt
      ? <small>{term.agreed ? "동의 시각" : "동의 상태 변경 시각"} {dateTime(term.agreedAt)}</small>
      : term.agreed && <small>동의 시각 정보 없음</small>}
  </>;
}

export function TermsPage() {
  const client = useQueryClient();
  const session = useSyncExternalStore(memberSessionStore.subscribe, memberSessionStore.get, memberSessionStore.get);
  const read = useRef<{ readonly session: ReturnType<typeof memberSessionStore.get>; readonly signal: AbortSignal } | undefined>(undefined);
  const previousSession = useRef(session);
  const dataOwner = useRef<ReturnType<typeof memberSessionStore.get> | undefined>(undefined);
  const query = useQuery({ queryKey: ["member", "terms"], queryFn: async ({ signal }) => {
    const owner = memberSessionStore.get(); read.current = { session: owner, signal };
    try {
      const result = await memberApi.terms();
      // Query cancellation consumes the signal even when the transport cannot abort.
      // Neither an old GET result nor its error may populate another owner's cache.
      if (signal.aborted || memberSessionStore.get() !== owner) throw new CancelledError({ revert: true });
      dataOwner.current = owner;
      return result;
    } catch (cause) {
      if (signal.aborted || memberSessionStore.get() !== owner) throw new CancelledError({ revert: true });
      throw cause;
    }
  } });
  const snapshot = useSyncExternalStore(memberRouter.subscribe, memberRouter.getSnapshot, memberRouter.getSnapshot);
  const selectedValue = new URLSearchParams(snapshot.location.search).get("termVersionId");
  const selectedNumber = selectedValue && /^\d+$/.test(selectedValue) ? Number(selectedValue) : undefined;
  const selectedId = selectedNumber && Number.isSafeInteger(selectedNumber) ? selectedNumber : undefined;
  const selected = useQuery({ queryKey: ["public", "term", selectedId], queryFn: () => publicApi.term(selectedId!), enabled: Boolean(selectedId) });
  const [failure, setFailure] = useState<FailedChange>();
  const [activeChange, setActiveChange] = useState<AgreementChange>();
  const pending = useRef(false);
  const mounted = useRef(false);
  const visit = useRef(0);
  const refetch = query.refetch;
  useEffect(() => {
    const changed = previousSession.current !== session; previousSession.current = session;
    mounted.current = true; visit.current += 1;
    pending.current = false; setActiveChange(undefined); setFailure(undefined);
    if (changed || dataOwner.current !== session) {
      const currentVisit = visit.current;
      // Cancellation alone leaves an empty query pending/idle. Start a new read
      // for the latest owner, and ignore a cancelled visit or another switch.
      const readCurrent = () => {
        if (session && mounted.current && visit.current === currentVisit && memberSessionStore.get() === session) void refetch({ cancelRefetch: false });
      };
      if (changed) void client.cancelQueries({ queryKey: ["member", "terms"] }).then(readCurrent);
      else readCurrent();
    }
    return () => { mounted.current = false; visit.current += 1; };
  }, [client, refetch, session]);
  const save = useMutation({ mutationFn: (request: AgreementChange) => memberApi.saveTermAgreement(request) });
  const change = async (request: AgreementChange) => {
    // A synchronous lock prevents two clicks before React renders pending state.
    const current = client.getQueryData<readonly TermAgreement[]>(["member", "terms"])?.find(term => term.termVersionId === request.termVersionId);
    if (pending.current || dataOwner.current !== memberSessionStore.get() || !current || current.required) return;
    const scope = { session: memberSessionStore.get(), visit: visit.current };
    const isCurrent = () => mounted.current && visit.current === scope.visit && memberSessionStore.get() === scope.session;
    pending.current = true; setActiveChange(request); setFailure(undefined);
    try {
      // Ignore an older query response that predates this mutation.
      await client.cancelQueries({ queryKey: ["member", "terms"] });
      const latest = client.getQueryData<readonly TermAgreement[]>(["member", "terms"])?.find(term => term.termVersionId === request.termVersionId);
      if (!isCurrent() || !latest || latest.required) return;
      const result = await save.mutateAsync(request);
      if (!isCurrent()) return;
      // Also cancel a focus/refetch request started while the save was pending.
      await client.cancelQueries({ queryKey: ["member", "terms"] });
      if (!isCurrent()) return;
      client.setQueryData<readonly TermAgreement[]>(["member", "terms"], previous => previous?.map(term => term.termVersionId === request.termVersionId ? result : term));
      await client.invalidateQueries({ queryKey: ["member", "terms"] });
    } catch (cause) {
      if (isCurrent()) setFailure({ request, message: cause instanceof ApiError || cause instanceof Error ? cause.message : "동의 상태를 저장하지 못했습니다. 다시 시도해 주세요." });
    } finally {
      if (isCurrent()) { pending.current = false; setActiveChange(undefined); }
    }
  };
  // A cached list from the previous owner cannot authorize a new owner's edit.
  const ownerPending = dataOwner.current !== session && (query.isFetching || !query.isError || read.current?.session !== session);
  if (query.isPending || ownerPending) return <LoadingState label="약관과 동의 상태를 불러오는 중입니다."/>;
  if (query.isError && (!query.data || dataOwner.current !== session)) return <ErrorState error={query.error} onRetry={() => void query.refetch()}/>;
  return <div className="member-terms-page">
    <p className="member-reading-intro">필수 약관은 실제 동의 기록을 확인하고, 선택 약관은 동의 상태를 변경할 수 있어요.</p>
    {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()}/>}
    <Surface>
      {!query.data?.length ? <EmptyState title="표시할 동의 항목이 없습니다" description="등록된 약관과 동의 상태는 고객센터 1522-5663으로 문의해 주세요."/>
        : <div className="terms-list">{query.data.map(term => <article key={term.termVersionId} aria-label={term.title}>
          <div className="member-terms-page__copy"><span>{term.required ? "필수" : "선택"}</span><strong>{term.title}</strong><AgreementStatus term={term}/>
            {failure?.request.termVersionId === term.termVersionId && <Alert persistent variant="danger" title="동의 상태를 저장하지 못했습니다"
              action={!term.required && <Button size="small" variant="quiet" disabled={Boolean(activeChange)} onClick={() => void change(failure.request)}>다시 시도</Button>}>
              {failure.message}
            </Alert>}
          </div>
          <div className="member-terms-page__actions">
            <Button size="small" variant="quiet" onClick={() => navigate(`/app/my/terms?termVersionId=${term.termVersionId}`)}>내용 보기</Button>
            {!term.required && <button type="button" role="switch" className="member-terms-page__switch"
              aria-label={`${term.title} 동의`} aria-checked={term.agreed}
              aria-busy={activeChange?.termVersionId === term.termVersionId}
              disabled={Boolean(activeChange)}
              onClick={() => void change({ termVersionId: term.termVersionId, agreed: !term.agreed })}>
              <span className="member-terms-page__switch-track" aria-hidden="true"><span className="member-terms-page__switch-thumb"/></span>
              <span className="member-terms-page__switch-label" aria-hidden="true">{activeChange?.termVersionId === term.termVersionId ? "저장 중" : term.agreed ? "동의함" : "동의 안 함"}</span>
            </button>}
          </div>
        </article>)}</div>}
    </Surface>
    <Surface title="개인정보처리방침" description="개인정보의 처리와 보관 기준을 안내하는 공개 문서입니다."><Button variant="quiet" onClick={() => navigate(MY_PRIVACY_POLICY_PATH)}>개인정보처리방침 보기</Button></Surface>
    <Dialog className="member-term-sheet" open={Boolean(selectedId)} onClose={() => navigate("/app/my/terms", true)} title="약관 내용">
      {selected.isPending && <LoadingState label="약관을 불러오는 중입니다."/>}
      {selected.isError && <ErrorState error={selected.error} onRetry={() => void selected.refetch()}/>}
      {selected.data && <LegalDocument term={selected.data}/>}
    </Dialog>
  </div>;
}
